import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CarrierItem, CarrierParams, UpdateShipmentDetail } from "@api/sharing/specialShipmentApi";
import PopupBackground from "@component/PopupBackground";
import useCityRegionCode from "@hooks/useCityRegionCode";
import {
  createSpecialShipment,
  fetchCarrierList,
  setOpenShipmentFormType,
  specialShipmentState,
  updateCarrierList,
  updateCreateSpecialShipmentId,
  updateSpecialShipment,
} from "@redux/sharing/specialShipmentSlice";
import { Button, Col, DatePicker, Form, Input, Modal, Radio, Row, Select, Space, Spin } from "antd";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { WarehouseCode, warehouseCodeOptionsUrmartDisabled } from "@constant/WarehouseCodeValue";
import { TcatStorageKey } from "@constant/StorageType";
import { commonSliceState, resetCommonAddress } from "@redux/sharing/commonSlice";
import {
  SpecialShipmentPurpose,
  specialShipmentPurposeOptions,
  CarrierType,
  CarrierCode,
} from "./specialShipmentConstants";


const PopupContent = styled.div`
  max-width: 70vw;
  padding: 20px;
  background: ${({ theme }) => theme.colorNeutral100};
  max-height: 90vh;
  overflow: auto;
  width: 500px;
  && .ant-form-item-required.ant-form-item-no-colon::before {
    display: none;
  }
  && .ant-form-item-required.ant-form-item-no-colon::after {
    display: inline-block;
    margin-right: 4px;
    color: #ff4d4f;
    font-size: 14px;
    font-family: SimSun, sans-serif;
    line-height: 1;
    content: "*";
  }
`;
const NoticeText = styled.div`
  color: ${({ theme }) => theme.colorSecondary500};
`;

type Props = {
  type?: "create" | "update";
  onClose: () => void;
};

enum AddressCode {
  OTHER = "OTHER",
}

const SpecialShipmentFormModal = (props: Props) => {
  const { type, onClose } = props;
  const dispatch = useDispatch();
  const {
    isFetching,
    carrierList,
    specialShipmentDetail,
    createSpecialShipmentId,
    specialShipmentProductList,
  } = useSelector(specialShipmentState);
  const { addressItem } = useSelector(commonSliceState);
  const [form] = Form.useForm();
  const { cityNameOptions, getCityRegionOptions } = useCityRegionCode();
  const [updateInit, setUpdateInit] = useState(false);
  const [addressState, setAddressState] = useState<{ required: boolean; disabled: boolean }>({
    required: true,
    disabled: false,
  });
  const warehouseCodeFormValue = Form.useWatch("warehouseCode", form);
  const purposeFormValue = Form.useWatch("purpose", form);
  const addressCodeFormValue = Form.useWatch("addressCode", form);
  const carrierIdFormValue = Form.useWatch("carrierId", form);
  const selectedCarrier = useMemo(() => carrierList?.find((item) => item.carrierId === carrierIdFormValue), [
    carrierList,
    carrierIdFormValue,
  ]);
  const carrierType = selectedCarrier?.carrierType;
  const { getPostCodeWithCVS } = useCityRegionCode();

  const disabledDate = (inputDate: any) => {
    return inputDate.isBefore(moment().subtract(1, "days")) || inputDate > moment().add(0.5, "y");
  };

  const handleGetCarrierChange = useCallback(
    (params?: CarrierParams) => {
      form.setFieldsValue({ carrierId: undefined });
      form.resetFields(["receiverName", "receiverPhone", "city", "region", "address"]);

      if (params) {
        dispatch(fetchCarrierList(params));
        return;
      }
      const warehouseCode = form.getFieldValue("warehouseCode");
      const purpose = form.getFieldValue("purpose");
      if (warehouseCode === WarehouseCode.BLACK_CAT) {
        // 倉庫選擇黑貓,地址會disabled,由"目的地"控制
        setAddressState({ required: true, disabled: true });
      } else {
        setAddressState((prevState) => ({ ...prevState, required: true, disabled: false }));
      }
      const payload = {
        warehouseCode,
        purpose,
        condition:
          warehouseCode === WarehouseCode.BLACK_CAT && purpose === SpecialShipmentPurpose.COMPENSATE
            ? TcatStorageKey.GOOD
            : undefined,
      };

      if (warehouseCode && purpose) {
        dispatch(fetchCarrierList(payload));
        form.setFieldsValue({ carrierId: undefined });
      }
    },
    [dispatch, form],
  );

  const handleOnSubmit = () => {
    const formData = form.getFieldsValue();
    const { estArrivedDate, estDeployedDate, region, city, address, addressCode, ...restData } = formData;
    const payload: UpdateShipmentDetail = {
      ...restData,
      estDeployedDate: estDeployedDate && estDeployedDate.format("YYYY-MM-DD"),
      estArrivedDate: estArrivedDate && estArrivedDate.format("YYYY-MM-DD"),
      addressCode: addressCode === AddressCode.OTHER ? null : addressCode,
    };

    if (carrierType === CarrierType.SELF_PICKUP) {
      // 廠商自取：送出“”清空收件人、收件電話、收件地址及郵遞區號欄位
      payload.receiverName = "";
      payload.receiverPhone = "";
      payload.receiverAddress = "";
      payload.receiverZipcode = "";
    } else if (region) {
      // 其他：有選擇區域才帶入地址
      payload.receiverZipcode = region.key;
      payload.receiverAddress = address ? `${city}${region.label}${address}` : undefined;
    }
    // 建立
    if (type === "create") {
      dispatch(createSpecialShipment(payload));
    }
    // 更新
    if (type === "update" && specialShipmentDetail) {
      const conditionChanged =
        formData.condition !== undefined &&
        formData.condition !== specialShipmentDetail.condition &&
        specialShipmentProductList.count > 0;

      if (conditionChanged) {
        Modal.confirm({
          title: "變更退倉良/壞品，將清空原本已建立的商品資料",
          okText: "確定",
          cancelText: "取消",
          onOk() {
            dispatch(updateSpecialShipment({ shipmentId: specialShipmentDetail.id, payload }));
          },
        });
      } else {
        dispatch(updateSpecialShipment({ shipmentId: specialShipmentDetail.id, payload }));
      }
    }
  };

  const handleOnClose = () => {
    onClose();
  };

  const handleClearAddress = useCallback(
    (warehouseCode: string, carrier?: CarrierItem) => {
      const shouldClear =
        (warehouseCode === WarehouseCode.BLACK_CAT && carrier?.carrierType !== CarrierType.SELF_PICKUP) ||
        carrier?.code === CarrierCode.CASH_ON_DELIVERY;

      if (shouldClear) {
        form.setFieldsValue({ addressCode: AddressCode.OTHER });
        setAddressState({ required: true, disabled: false });
        dispatch(resetCommonAddress());
        form.resetFields(["receiverName", "receiverPhone", "city", "region", "address"]);
      }
    },
    [dispatch, form],
  );

  useEffect(() => {
    if (createSpecialShipmentId) {
      window.open(`/special-shipment/edit/${createSpecialShipmentId}`, "_blank");
      dispatch(updateCreateSpecialShipmentId(undefined));
      dispatch(setOpenShipmentFormType(undefined));
    }
  }, [createSpecialShipmentId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(updateCarrierList(undefined));
    };
  }, [dispatch]);

  useEffect(() => {
    if (type === "create") return;
    if (!updateInit && specialShipmentDetail) {
      const { receiverZipcode, receiverAddress, addressCode } = specialShipmentDetail;
      if (carrierList?.length) {
        form.setFieldsValue({
          ...specialShipmentDetail,
          addressCode: addressCode === null ? AddressCode.OTHER : addressCode,
          receiverZipcode,
          address: getPostCodeWithCVS(receiverAddress).address,
          city: getPostCodeWithCVS(receiverAddress).city,
          region: {
            label: getPostCodeWithCVS(receiverAddress).town,
            value: receiverZipcode,
          },
          estDeployedDate: specialShipmentDetail.estDeployedDate
            ? moment(specialShipmentDetail.estDeployedDate)
            : undefined,
          estArrivedDate: specialShipmentDetail.estArrivedDate
            ? moment(specialShipmentDetail.estArrivedDate)
            : undefined,
        });
        const carrier = carrierList?.find((item) => item.carrierId === specialShipmentDetail.carrierId);

        if (carrier?.carrierType === CarrierType.SELF_PICKUP) {
          setAddressState({ required: false, disabled: false });
        }
        setUpdateInit(true);
      } else {
        handleGetCarrierChange({
          warehouseCode: specialShipmentDetail.warehouseCode,
          purpose: specialShipmentDetail.purpose,
          condition: specialShipmentDetail.condition,
        });
      }
    }
  }, [carrierList, form, getPostCodeWithCVS, handleGetCarrierChange, specialShipmentDetail, type, updateInit]);

  const handleSelectCity = () => {
    form.setFieldsValue({ region: undefined });
  };

  useEffect(() => {
    if (addressCodeFormValue && addressItem) {
      setAddressState({ required: true, disabled: true });
      const selectedAddress = {
        receiverName: addressItem.name,
        receiverPhone: addressItem.phone,
        address: getPostCodeWithCVS(addressItem.address).address,
        city: getPostCodeWithCVS(addressItem.address).city,
        region: {
          key: getPostCodeWithCVS(addressItem.address).code,
          value: getPostCodeWithCVS(addressItem.address).town,
          label: getPostCodeWithCVS(addressItem.address).town,
        },
      };

      form.setFieldsValue(selectedAddress);
    }
  }, [addressCodeFormValue, addressItem, form, getPostCodeWithCVS, type]);

  return (
    <PopupBackground close={() => {}}>
      <PopupContent>
        <Form form={form} colon={false} labelCol={{ span: 6 }} labelAlign="left" onFinish={handleOnSubmit}>
          <Spin spinning={isFetching}>
            <Form.Item name="warehouseCode" label="出庫倉庫" rules={[{ required: true, message: "" }]}>
              <Select
                options={warehouseCodeOptionsUrmartDisabled}
                onChange={() => handleGetCarrierChange()}
                placeholder="請選擇"
                disabled={type === "update"}
              />
            </Form.Item>
            <Form.Item name="estDeployedDate" label="預期拋單日期" rules={[{ required: true, message: "" }]}>
              <DatePicker placeholder="請選擇日期" disabledDate={disabledDate} />
            </Form.Item>
            <Form.Item
              name="estArrivedDate"
              label="預計到貨日期"
              extra={<NoticeText>*此資訊用於顯示倉庫備註中，僅供倉庫人員參考</NoticeText>}
            >
              <DatePicker placeholder="請選擇日期" disabledDate={disabledDate} />
            </Form.Item>
            <Form.Item
              label="訂購緣由"
              name="purpose"
              rules={[{ required: true, message: "請填入訂購緣由" }]}
              extra={
                <>
                  <NoticeText>
                    *行銷推廣：行銷推廣 / 公關品 / 抽獎贈品 / 提品 / 試吃品請選擇此項目，並於說明補充用途
                  </NoticeText>
                  <NoticeText>*賠償：僅限客服補貨使用或是將壞品寄回饌元辦公室</NoticeText>
                  <NoticeText>*退倉：將商品退還給廠商</NoticeText>
                  <NoticeText>*倉庫出貨耗材：將包材送到廠商之用途</NoticeText>
                </>
              }
            >
              <Select
                placeholder="請選擇"
                options={specialShipmentPurposeOptions}
                onChange={() => handleGetCarrierChange()}
              />
            </Form.Item>
            {((warehouseCodeFormValue === WarehouseCode.BLACK_CAT &&
              purposeFormValue === SpecialShipmentPurpose.COMPENSATE) ||
              purposeFormValue === SpecialShipmentPurpose.WITHDRAW) && (
              <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.purpose !== curValues.purpose}>
                {({ getFieldValue }) => {
                  const condition = getFieldValue("condition");
                  form.setFieldValue("condition", condition || "GOOD");

                  return (
                    <Form.Item wrapperCol={{ span: 8 }} label=" " name="condition">
                      <Radio.Group
                        onChange={() => {
                          const latestCondition = form.getFieldValue("condition");
                          if (
                            warehouseCodeFormValue === WarehouseCode.BLACK_CAT &&
                            purposeFormValue === SpecialShipmentPurpose.COMPENSATE
                          ) {
                            dispatch(
                              fetchCarrierList({
                                warehouseCode: warehouseCodeFormValue,
                                purpose: purposeFormValue,
                                condition: latestCondition,
                              }),
                            );
                          }
                        }}
                      >
                        <Radio value="GOOD" defaultChecked>
                          良品
                        </Radio>
                        <Radio value="BAD">壞品</Radio>
                      </Radio.Group>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            )}
            <Form.Item
              label="緣由說明"
              name="desc"
              rules={[
                {
                  required: true,
                  message: "說明最少5字，最多30字",
                  max: 30,
                  min: 5,
                },
              ]}
            >
              <Input placeholder="最少5字，最多30字" />
            </Form.Item>
            <Form.Item label="運送方式" name="carrierId" rules={[{ required: true, message: "" }]}>
              <Select
                placeholder={warehouseCodeFormValue && purposeFormValue ? "請選擇" : "請先選擇出庫倉庫和訂購緣由"}
                options={carrierList?.map((item) => ({ label: item.name, value: item.carrierId }))}
                disabled={!warehouseCodeFormValue || !purposeFormValue}
                onChange={(carrierId) => {
                  const carrier = carrierList?.find((item) => item.carrierId === carrierId);
                  if (carrier?.carrierType === CarrierType.SELF_PICKUP) {
                    setAddressState({ required: false, disabled: false });
                    form.resetFields(["receiverName", "receiverPhone", "city", "region", "address"]);
                    setUpdateInit(true);
                    return;
                  }
                  setAddressState({ required: true, disabled: false });
                  handleClearAddress(warehouseCodeFormValue, carrier);
                }}
              />
            </Form.Item>
            {((warehouseCodeFormValue === WarehouseCode.BLACK_CAT && carrierType !== CarrierType.SELF_PICKUP) ||
              selectedCarrier?.code === CarrierCode.CASH_ON_DELIVERY) && (
              <>
                <Form.Item name="addressCode" hidden>
                  <Input />
                </Form.Item>
                <Form.Item label="目的地">
                  <Input value="其他" disabled />
                </Form.Item>
              </>
            )}

            {addressState.required && (
              <>
                <Form.Item name="receiverName" label="收件人" rules={[{ required: true, message: "" }]}>
                  <Input type="text" disabled={addressState.disabled} />
                </Form.Item>
                <Form.Item label="收件電話" name="receiverPhone" rules={[{ required: true, message: "" }]}>
                  <Input disabled={addressState.disabled} />
                </Form.Item>
                <Form.Item label="收件地址" required>
                  <Row gutter={[4, 0]}>
                    <Col span={12}>
                      <Form.Item name="city" rules={[{ required: true, message: "" }]}>
                        <Select
                          options={cityNameOptions}
                          placeholder="請選擇縣市"
                          onChange={handleSelectCity}
                          disabled={addressState.disabled}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item shouldUpdate={(prevValues, curValues) => prevValues.city !== curValues.city} noStyle>
                        {({ getFieldValue }) => {
                          const city = getFieldValue("city");
                          return (
                            <Form.Item name="region" rules={[{ required: true, message: "" }]}>
                              <Select
                                labelInValue
                                options={city ? getCityRegionOptions(city) : []}
                                disabled={form.getFieldValue("city") === undefined || addressState.disabled}
                                placeholder="請選擇區域"
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[4, 0]}>
                    <Col flex="auto">
                      <Form.Item name="address" rules={[{ required: true, message: "" }]}>
                        <Input type="text" disabled={addressState.disabled} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </>
            )}
            <Row justify="end">
              <Space>
                <Button type="default" onClick={() => handleOnClose()}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit">
                  確認
                </Button>
              </Space>
            </Row>
          </Spin>
        </Form>
      </PopupContent>
    </PopupBackground>
  );
};

export default SpecialShipmentFormModal;
